<template>
	<div class="theme--map" style="height: 100%">

		<gmap-map
			@zoom_changed="zoomChanged"
			:center="location"
			:zoom="zoom"
			:options="mapOptions"
			class="map"
			ref="map">

			<device-cluster>
				<device-marker
					v-for="device in devices"
					:device="device"
					:key="device.id"
					@click="gotoDeviceDetail(device)">
				</device-marker>
			</device-cluster>

			<user-marker v-if="showUserLocation"></user-marker>

		</gmap-map>

		<router-view></router-view>

		<v-layout v-show="isLoading" align-center justify-center fill-height class="load-overlay">
			<v-progress-circular indeterminate color="primary"></v-progress-circular>
		</v-layout>

	</div>
</template>

<script>
  import { gmapApi } from 'vue2-google-maps';

  import mapStyles from '../../assets/map/map.styles';
  import GeometryService from '../../services/GeometryService';
  import DeviceCluster from '../../components/Map/DeviceCluster';
  import DeviceMarker from '../../components/Map/DeviceMarker';
  import UserMarker from '../../components/Map/UserMarker';

  export default {
    components: {
      DeviceCluster,
      DeviceMarker,
      UserMarker,
    },
    timers: {
      refreshDevices: { time: 30000, autostart: true, repeat: true },
    },
    provide() {
      return {
        mapComponent: this.mapComponent,
      };
    },
    computed: {
      google: gmapApi,
      showUserLocation() {
        return this.device && this.device.isDetectingRange() && this.userLocation;
      },
	  allDevices() {
        return this.$store.getters.devices;
	  },
      devices() {
        if (this.device) return [this.device];
        return this.$store.getters.activeDevices;
      },
      device() {
        return this.$store.getters.device;
      },
      mapType() {
        return this.$store.state.map.mapTypeId;
      },
      isMainMenuOpen() {
        return this.$store.state.isMainMenuOpen;
      },
      userLocation() {
        return this.$store.state.location.coordinates;
      }
    },
    watch: {
      devices() {
        if (!this.device) {
          this.$refs.map.$el.style.bottom = '0px';
        }
      },
      device(value) {
        if (value && value.getLocation) return this.showMarker();
        return this.showAllMarkers();
      },
      mapType(value) {
        this.mapOptions = { ...this.mapOptions, mapTypeId: value };
      },
    },
    data: () => ({
      isLoading: false,
      afterLoading: null,
      location: { lat: 52.19, lng: 5.2792555 },
      zoom: 7,
      zoomType: 'user',
      userDefinedZoom: false,
      isRunningInBrowser: process.env.CORDOVA_PLATFORM === 'browser',
      mapOptions: {
        styles: mapStyles,
        gestureHandling: 'greedy',
        disableDefaultUI: true,
        animatedZoom: false,
        mapTypeId: 'roadmap',
      },
    }),
    methods: {
      mapComponent() {
        return this.$refs.map;
      },

      gotoDeviceDetail(device) {
        if (!this.isLoading) {
          this.$router.push({ name: 'devices.show', params: { id: device.id } });
        } else {
          this.afterLoading = () => {
            this.$router.push({ name: 'devices.show', params: { id: device.id } });
          };
        }
      },

      fitToScreen() {
        setTimeout(() => {
          const bounds = new this.google.maps.LatLngBounds();

          if (this.devices.length > 0) {
            this.devices.forEach((device) => {
              const offset = Math.sqrt(2) * device.getAccuracy();
              const location = device.getLocation();
              bounds.extend(GeometryService.locationOffset(location, -45, offset));
              bounds.extend(GeometryService.locationOffset(location, 135, offset));
            });
          } else {
            // Show netherlands
            bounds.extend({ lat: 53.6316, lng: 7.22751029 });
            bounds.extend({ lat: 50.7503838, lng: 3.3316001 });
          }

          this.zoomType = 'app';

          this.$refs.map.$mapObject.fitBounds(bounds);

          if (this.devices.length === 1) {
            this.zoomType = 'app';
            this.$refs.map.$mapObject.setZoom(this.devices[0].getZoomLevel());
          }
        }, 500);
      },
      showAllMarkers() {
        this.userDefinedZoom = false;

        this.$refs.map.$mapPromise.then(() => {
          this.fitToScreen();
        });
      },
      showMarker() {
        if (!this.device.isShowingHistory()) {
          this.userDefinedZoom = false;
        }

        this.$refs.map.$el.style.bottom = '150px';
        this.$refs.map.$mapObject.panTo(this.device.getLocation());

        if (!this.device.isShowingHistory() || this.device.getZoomLevel() < this.zoom) {
          this.zoomType = 'app';

          if (this.device.isDetectingRange() && !this.device.isShowingHistory()) {
            const bounds = new this.google.maps.LatLngBounds();
            bounds.extend(this.device.getLocationWithCircleOffset());
            bounds.extend(this.userLocation);
            this.$refs.map.$mapObject.fitBounds(bounds);
          } else {
            this.$refs.map.$mapObject.setZoom(this.device.getZoomLevel());
          }


          this.userDefinedZoom = false;
        }
      },
      refreshDevices() {
        if (this.device) return;
        this.$store.dispatch('loadDevices')
          .then(() => {
            this.isLoading = false;
            if (typeof this.afterLoading === 'function') this.afterLoading();
            this.afterLoading = null;
          });
      },
	  async loadDevices() {
        this.isLoading = true;
        const devices = await this.$store.dispatch('loadDevices');

		if(!this.$store.state.map.redirectedFromMap) {
          this.$store.commit('redirectFromMap');

          // Redirect if there are not any registered devices
          if (!this.allDevices.length && this.$router.currentRoute.name == 'devices.index') {
            this.isLoading = false;

            return this.isRunningInBrowser
              ? this.$router.replace({ name: 'devices.add' })
              : this.$router.replace({ name: 'devices.list' });
          }

          // Redirect to list if an device is waiting for activation & in app (Koppelen)
          if (
            this.isRunningInBrowser &&
            this.allDevices.find(device => device.isWaitingForActivation()) &&
		  	this.$router.currentRoute.name == 'devices.index'
          ) {
            this.isLoading = false;
            return this.$router.replace({ name: 'devices.list' });
          }
        }

	  	return this.loadMap();
	  },

      async loadMap() {
        if (this.device) return this.isLoading = false;
        this.isLoading = true;

        await this.$refs.map.$mapPromise;

		this.showAllMarkers();
		this.isLoading = false;
		if (typeof this.afterLoading === 'function') this.afterLoading();
		this.afterLoading = null;
      },
      zoomChanged(zoomLevel) {
        if (this.zoomType === 'user') {
          this.userDefinedZoom = true;
        }
        this.zoomType = 'user';
        this.zoom = zoomLevel;
      }
    },
    mounted() {
      this.loadDevices();
    },
	created() {
      this.$store.dispatch('startTracking');
    }
  };
</script>

<style lang="stylus">
	.map.vue-map-container
		position fixed
		top: 4rem
		left: 0
		bottom: 0
		right: 0

	.label-gps
		&:after
			content: ''
			position: fixed
			display: block
			width: 50px
			height: 50px
			background: red
			z-index: 9999

	.load-overlay
		position fixed
		top: 4rem
		left: 0
		bottom: 0
		right: 0
		background: #fafafa
</style>


